export class ChecklistsApi {
  #http;
  #loansHttp;

  constructor(http, loansHttp) {
    this.#http = http;
    this.#loansHttp = loansHttp;
  }

  async getChecklists({ skipForbiddenError, ...params }) {
    return this.#http.get('/checklists', {
      params,
      ...(skipForbiddenError && { skipResponseInterceptor: { forbiddenError: true } }),
    });
  }

  // Searches checklists with a full filter body — notably `includeHidden`, which the plain
  // `/checklists` list does not expose. The completeness-checklist picker needs it because a
  // completeness checklist is a HIDDEN decisioning engine and is otherwise unlistable (DEV-4603).
  async searchChecklists({ companyId, skipForbiddenError, ...filter }) {
    return this.#loansHttp.post('/api/checklists/search', filter, {
      ...(companyId && { params: { companyId } }),
      ...(skipForbiddenError && { skipResponseInterceptor: { forbiddenError: true } }),
    });
  }

  async getChecklist({ checklistId, ...params }) {
    return this.#http.get(`/checklists/${checklistId}`, { params });
  }

  // One node's own expressions. The checklist-wide read is the only alternative, and for an item that
  // means every item's expression bodies (FH-1649: 3.6MB to open one row of a large checklist).
  async getChecklistCategory({ checklistId, categoryId }) {
    return this.#loansHttp.get(`/api/checklists/${checklistId}/categories/${categoryId}`);
  }

  async getChecklistItem({ checklistId, itemId }) {
    return this.#loansHttp.get(`/api/checklists/${checklistId}/items/${itemId}`);
  }

  async getApplicationChecklistsResult({ applicationId, ...params }) {
    return this.#http.get(`/checklists/result/${applicationId}`, { params });
  }

  async getChecklistsExecutionSnapshots({ applicationId, ...params }) {
    // Loan-editor secondary read: degrade (not global reload loop) on a subtree-scoped TPO
    // broker 403 (DEV-4226).
    return this.#http.get(`checklist-execution-snapshots/${applicationId}`, {
      params,
      skipResponseInterceptor: { forbiddenError: true },
    });
  }

  // Loan-scoped checklist results carry each checklist's `isHidden` flag and are gated by the
  // loan-view claim (not the admin ChecklistsManagement claim the `/checklists` list requires), so
  // this is the read a plain LO can make to learn which of the loan's checklists are the hidden
  // completeness engines that drive the in-editor "Needed" highlighting (DEV-4603).
  async getLoanChecklistResults({ applicationId, ...params }) {
    return this.#loansHttp.get(`/api/loanapplications/${applicationId}/checklist-result`, {
      params,
    });
  }

  // FH-1931: the FH-1829 flat-store lean read — same `ChecklistSnapshotsQueryFilters` body as
  // `getChecklistsExecutionSnapshots` above, called directly against Loans (not the SkynetAPI
  // legacy proxy), skipping that extra ser-des hop. Returns `FlatChecklistResult[]` raw (no `{data}`
  // envelope), unlike the legacy snapshots read. Keeps the same subtree-scoped TPO-broker 403
  // degrade (not a global reload loop) the legacy read carries (DEV-4226).
  async getFlatChecklistResults(filters) {
    return this.#loansHttp.post('/api/checklist-results/v2', filters, {
      skipResponseInterceptor: { forbiddenError: true },
    });
  }

  async editChecklist({ skipRecalculation, ...body }) {
    return this.#http.put(`/checklists/${body.checklistId}`, body, {
      params: { skipRecalculation },
    });
  }

  async createChecklist(body) {
    return this.#http.post('/checklists', body);
  }

  async createChecklistCategory(body) {
    return this.#http.post(`/checklists/${body.checklistId}/categories`, body);
  }

  async createChecklistItem({ checklistId, categoryId, ...body }) {
    return this.#http.post(`/checklists/${checklistId}/categories/${categoryId}/items`, body);
  }

  async changeApplicationChecklistResult({
    applicationId,
    checklistId,
    targetId,
    targetType,
    ...body
  }) {
    return this.#http.post(
      `/api/applications/${applicationId}/checklist/${checklistId}/result/changes`,
      body,
      {
        // `targetId`/`targetType` are query params on the server override endpoint (DEV-4728) —
        // they scope the override to a per-borrower snapshot; omitted for loan-level fields, where
        // the server defaults to the application/LoanApplication target. Axios drops undefined params.
        params: { applicationId, targetId, targetType },
      },
    );
  }

  async deleteChecklist({ checklistId }) {
    return this.#http.delete(`/checklists/${checklistId}`);
  }

  async deleteChecklistCategory({ checklistId, categoryId }) {
    return this.#http.delete(`/checklists/${checklistId}/categories/${categoryId}`);
  }

  async deleteChecklistItem({ checklistId, categoryId, itemId }) {
    return this.#http.delete(`/checklists/${checklistId}/categories/${categoryId}/items/${itemId}`);
  }

  async disableGlobalChecklist({ checklistId }) {
    return this.#loansHttp.post(`/api/checklists/${checklistId}/disable`);
  }

  async enableGlobalChecklist({ checklistId }) {
    return this.#loansHttp.post(`/api/checklists/${checklistId}/enable`);
  }

  async cloneGlobalChecklist({ checklistId }) {
    return this.#loansHttp.post(`/api/checklists/${checklistId}/clone`);
  }

  async disableGlobalChecklistNode({ categoryId, itemId }) {
    return this.#loansHttp.post('/api/checklists/nodes/disable', null, {
      params: { categoryId, itemId },
    });
  }

  async enableGlobalChecklistNode({ categoryId, itemId }) {
    return this.#loansHttp.post('/api/checklists/nodes/enable', null, {
      params: { categoryId, itemId },
    });
  }

  async setChecklistVisibilityOverride(body) {
    return this.#loansHttp.post('/api/checklists/visibility-override', body);
  }

  async removeChecklistVisibilityOverride({ checklistId, categoryId, itemId }) {
    return this.#loansHttp.delete('/api/checklists/visibility-override', {
      params: { checklistId, categoryId, itemId },
    });
  }
}
